import { lazy } from "react";
import { ROUTES } from "@/shared/config/routes";
import { RequireSignupTicket } from "@/features/auth";
import type { RouteConfig } from "./types";

const Login = lazy(() => import("@/pages/auth/Login"));
const Private = lazy(() => import("@/pages/private/Private"));
const ComponentShowcase = lazy(() => import("@/pages/example/ComponentShowcase"));
const OAuthCallback = lazy(() => import("@/pages/auth/OAuthCallback"));
const SignupStepDemoPage = lazy(() => import("@/pages/signup/demo/SignupStepDemoPage"));
const SignupOnboardingDemoPage = lazy(() => import("@/pages/signup/demo/SignupOnboardingDemoPage"));
const SignupTermsPage = lazy(() => import("@/pages/signup/SignupTermsPage"));
const SignupIdentityPage = lazy(() => import("@/pages/signup/SignupIdentityPage"));
const SignupCreditRegisterPage = lazy(
  () => import("@/pages/signup/credit/SignupCreditRegisterPage"),
);
const SignupCreditLoginPage = lazy(
  () => import("@/pages/signup/credit/SignupCreditLoginPage"),
);
const SignupCreditFindIdPage = lazy(
  () => import("@/pages/signup/credit/SignupCreditFindIdPage"),
);
const SignupCreditPasswordResetPage = lazy(
  () => import("@/pages/signup/credit/SignupCreditPasswordResetPage"),
);
const SignupOnboardingPage = lazy(
  () => import("@/pages/signup/SignupOnboardingPage"),
);
const CreditPasswordRenewalPage = lazy(
  () => import("@/pages/credit4u/CreditPasswordRenewalPage"),
);
const CreditPasswordResetPage = lazy(
  () => import("@/pages/credit4u/CreditPasswordResetPage"),
);
const Terms = lazy(() => import("@/pages/legal/Terms"));
const FeeDetail = lazy(() => import("@/pages/fees/FeeDetail"));
const ExamHistory = lazy(() => import("@/pages/more/ExamHistory"));
const ExamHistoryDetail = lazy(() => import("@/pages/more/ExamHistoryDetail"));
const CommissionLife = lazy(() => import("@/pages/more/CommissionLife"));
const CommissionNonLife = lazy(() => import("@/pages/more/CommissionNonLife"));
const CommissionLifeSearch = lazy(() => import("@/pages/more/CommissionLifeSearch"));
const CommissionNonLifeSearch = lazy(() => import("@/pages/more/CommissionNonLifeSearch"));
const ProfileEdit = lazy(() => import("@/pages/profile/ProfileEdit"));
const WithdrawIntro = lazy(() => import("@/pages/profile/WithdrawIntro"));
const WithdrawComplete = lazy(() => import("@/pages/profile/WithdrawComplete"));
const InviteCodeRegister = lazy(() => import("@/pages/invite-code/InviteCodeRegister"));
const Organization = lazy(() => import("@/pages/organization/Organization"));
const OrganizationMemberDetail = lazy(
  () => import("@/pages/organization/OrganizationMemberDetail"),
);
const ContractHistory = lazy(() => import("@/pages/contract-history/ContractHistory"));
const CommissionHistory = lazy(() => import("@/pages/commission-history/CommissionHistory"));
const CommissionStatementDetail = lazy(
  () => import("@/pages/commission-history/CommissionStatementDetail"),
);
const ExamApply = lazy(() => import("@/pages/exam/ExamApply"));
const ExamPaymentResult = lazy(() => import("@/pages/exam/ExamPaymentResult"));
const PaymentReturn = lazy(() => import("@/pages/payment/PaymentReturn"));

/** Route 설정. 새 라우트는 access 만 쓴다(레거시 type 은 resolveAccess 가 변환). */
// `/`(ROUTES.ROOT) 는 이 배열에 없다 — 화면이 아니라 라우팅 규칙이라
// Router.tsx 가 AuthGuard 없이 RootRedirect 로 직접 처리한다 (해당 주석 참조).
export const routes: RouteConfig[] = [
  {
    path: ROUTES.LOGIN,
    element: <Login />,
    access: "guestOnly",
  },
  {
    path: ROUTES.OAUTH_CALLBACK,
    element: <OAuthCallback />,
    access: "public",
  },
  // 탭 라우트 4개: Router.tsx 가 isTabRoute 분기에서 TabShell 을 직접 렌더한다 (access 정책 참조용 엔트리).
  // element 는 절대 렌더되지 않으므로 null 로 둔다.
  {
    path: ROUTES.MAIN,
    element: null,
    access: "authenticated",
  },
  {
    path: ROUTES.FEES,
    element: null,
    access: "authenticated",
  },
  {
    path: ROUTES.COVERAGE_ANALYSIS,
    element: null,
    access: "authenticated",
  },
  {
    path: ROUTES.MORE,
    element: null,
    access: "authenticated",
  },
  {
    path: ROUTES.PRIVATE,
    element: <Private />,
    access: "public",
  },
  {
    path: ROUTES.EXAMPLE,
    element: <ComponentShowcase />,
    access: "public",
  },
  // `/temp`(외부 홈페이지 임시 리다이렉트)는 여기 없다 — AuthGuard 밖 전용 라우트(Router.tsx 의 TempEntry).
  // 이용약관/개인정보처리방침 — 무로그인 public 페이지(앱 심사 링크). 햄버거 메뉴 진입 + URL 직접 진입 공용.
  {
    path: ROUTES.TERMS,
    element: <Terms />,
    access: "public",
  },
  {
    path: ROUTES.SIGNUP_STEP_DEMO,
    element: <SignupStepDemoPage />,
    access: "public",
  },
  {
    path: ROUTES.SIGNUP_ONBOARDING_DEMO,
    element: <SignupOnboardingDemoPage />,
    access: "public",
  },
  // 가입 본류(terms/identity/register/credit-login)는 signupTicket 가드 적용 —
  // 티켓 없으면 로그인으로 (#74, TTL 검증은 서버 위임).
  {
    path: ROUTES.SIGNUP_TERMS,
    element: (
      <RequireSignupTicket>
        <SignupTermsPage />
      </RequireSignupTicket>
    ),
    access: "public",
  },
  {
    path: ROUTES.SIGNUP_IDENTITY,
    element: (
      <RequireSignupTicket>
        <SignupIdentityPage />
      </RequireSignupTicket>
    ),
    access: "public",
  },
  {
    path: ROUTES.SIGNUP_CREDIT_REGISTER,
    element: (
      <RequireSignupTicket>
        <SignupCreditRegisterPage />
      </RequireSignupTicket>
    ),
    access: "public",
  },
  {
    path: ROUTES.SIGNUP_CREDIT_LOGIN,
    element: (
      <RequireSignupTicket>
        <SignupCreditLoginPage />
      </RequireSignupTicket>
    ),
    access: "public",
  },
  // find-id/password-reset 은 의도적 무인증 복구 경로 (서버 permitAll + CODEF 2-Way 책임)
  // — 시작 화면은 무가드 공개. 완료는 별도 페이지 없이 팝업 단일 [로그인하기]로 신용정보원 로그인 이동 (#321).
  {
    path: ROUTES.SIGNUP_CREDIT_FIND_ID,
    element: <SignupCreditFindIdPage />,
    access: "public",
  },
  {
    path: ROUTES.SIGNUP_CREDIT_RESET_PASSWORD,
    element: <SignupCreditPasswordResetPage />,
    access: "public",
  },
  {
    path: ROUTES.SIGNUP_ONBOARDING,
    element: <SignupOnboardingPage />,
    access: "public",
  },
  // 신용정보원 비밀번호 만료 갱신 경량 화면 — 앱 내부 진입(만료 팝업 [변경하기]), 인증 필요 (#149)
  {
    path: ROUTES.CREDIT_PASSWORD_RENEWAL,
    element: <CreditPasswordRenewalPage />,
    access: "authenticated",
  },
  // OUT_OF_SYNC 복구 진입 전용 — 신용정보원 비밀번호 desync 고착 시 재설정+resync 체이닝 세션 화면 (#213)
  {
    path: ROUTES.CREDIT_PASSWORD_RESET,
    element: <CreditPasswordResetPage />,
    access: "authenticated",
  },
  { path: ROUTES.FEES_DETAIL, element: <FeeDetail />, access: "authenticated" },
  { path: ROUTES.EXAM_HISTORY, element: <ExamHistory />, access: "authenticated" },
  { path: ROUTES.EXAM_HISTORY_DETAIL, element: <ExamHistoryDetail />, access: "authenticated" },
  { path: ROUTES.COMMISSION_LIFE, element: <CommissionLife />, access: "authenticated" },
  { path: ROUTES.COMMISSION_NONLIFE, element: <CommissionNonLife />, access: "authenticated" },
  {
    path: ROUTES.COMMISSION_LIFE_SEARCH,
    element: <CommissionLifeSearch />,
    access: "authenticated",
  },
  {
    path: ROUTES.COMMISSION_NONLIFE_SEARCH,
    element: <CommissionNonLifeSearch />,
    access: "authenticated",
  },
  { path: ROUTES.ACCOUNT, element: <ProfileEdit />, access: "authenticated" },
  // 회원 탈퇴 플로우 (#230) — 인증 필요. 경고(Spec 15) → 완료(Spec 16)
  { path: ROUTES.ACCOUNT_WITHDRAW, element: <WithdrawIntro />, access: "authenticated" },
  {
    path: ROUTES.ACCOUNT_WITHDRAW_COMPLETE,
    element: <WithdrawComplete />,
    access: "authenticated",
  },
  { path: ROUTES.INVITE_CODE_REGISTER, element: <InviteCodeRegister />, access: "authenticated" },
  { path: ROUTES.ORGANIZATION, element: <Organization />, access: "authenticated" },
  {
    path: ROUTES.ORGANIZATION_MEMBER_DETAIL,
    element: <OrganizationMemberDetail />,
    access: "authenticated",
  },
  // 계약 내역 / 수수료 내역 — 더보기 「내 정보」 진입점 (#348).
  { path: ROUTES.CONTRACT_HISTORY, element: <ContractHistory />, access: "authenticated" },
  { path: ROUTES.COMMISSION_HISTORY, element: <CommissionHistory />, access: "authenticated" },
  {
    path: ROUTES.COMMISSION_HISTORY_MONTH,
    element: <CommissionStatementDetail />,
    access: "authenticated",
  },
  { path: ROUTES.EXAM, element: <ExamApply />, access: "authenticated" },
  {
    path: ROUTES.EXAM_PAYMENT_RESULT,
    element: <ExamPaymentResult />,
    access: "authenticated",
  },
  {
    // public — 토큰 없는 외부/인앱 브라우저(간편결제 복귀)에서도 도달해야 "앱으로 돌아가기" 뷰가 뜬다.
    // 앱 WebView(토큰 보유) poll 경로는 API 호출에 Bearer 를 계속 붙이므로 접근 완화와 무관.
    path: ROUTES.PAYMENT_RETURN,
    element: <PaymentReturn />,
    access: "public",
  },
];

export default routes;
